/**
 * App — main coordinator, assembles all modules and manages lifecycle
 */

import Config from './Config.js';
import EventBus from './EventBus.js';
import StateManager from './StateManager.js';
import ApiClient from '../api/ApiClient.js';
import SessionManager from '../api/SessionManager.js';
import StyleManager from './StyleManager.js';
import I18n from '../i18n/I18n.js';
import { EVENTS } from './events.js';

// Services
import SearchService from '../services/SearchService.js';
import SuggestService from '../services/SuggestService.js';
import FilterService from '../services/FilterService.js';
import WidgetParamsService from '../services/WidgetParamsService.js';

// UI components
import Panel from '../ui/Panel.js';
import SearchBar from '../ui/SearchBar.js';
import SuggestDropdown from '../ui/SuggestDropdown.js';
import FiltersBar from '../ui/FiltersBar.js';
import ResultsGrid from '../ui/ResultsGrid.js';
import CropOverlay from '../ui/CropOverlay.js';
import PhotoUpload from '../ui/PhotoUpload.js';
import LoadingState from '../ui/LoadingState.js';
import EmptyState from '../ui/EmptyState.js';
import ErrorState from '../ui/ErrorState.js';

export default class App {
  /**
   * @param {Object} options - Raw options from GarderoboSearch.init()
   */
  constructor(options) {
    this.config = new Config(options);
    this.eventBus = new EventBus();
    this.stateManager = new StateManager();

    // API layer
    this.apiClient = new ApiClient(this.config.apiBaseUrl, this.config.apiKey);
    this.sessionManager = new SessionManager(this.apiClient);

    // Styles
    this.styleManager = new StyleManager();

    // Localization
    this.i18n = new I18n(this.config.locale);

    this._targetElement = null;
    this._destroyed = false;

    // UI component references
    this.panel = null;
    this.searchBar = null;
    this.suggestDropdown = null;
    this.filtersBar = null;
    this.resultsGrid = null;
    this.cropOverlay = null;
    this.photoUpload = null;
    this.loadingState = null;
    this.emptyState = null;
    this.errorState = null;

    // Services
    this.filterService = new FilterService(this.stateManager, this.eventBus, this.config);
    this.searchService = null;
    this.suggestService = null;
    this.widgetParamsService = new WidgetParamsService(this.apiClient, this.styleManager, this.i18n);

    this._widgetParams = null;

    // Bound handler for target element events
    this._onTargetFocus = this._handleTargetFocus.bind(this);
    this._onTargetInput = this._handleTargetInput.bind(this);
    this._onTargetKeydown = this._handleTargetKeydown.bind(this);
    this._onFormSubmit = this._handleFormSubmit.bind(this);
    this._targetForm = null;
  }

  /**
   * Full initialization lifecycle
   */
  async init() {
    if (this._destroyed) {
      throw new Error('[GarderoboSearch] Cannot init a destroyed instance');
    }

    // 1. Resolve target element
    this._targetElement = document.querySelector(this.config.selector);
    if (!this._targetElement) {
      throw new Error(
        `[GarderoboSearch] Element not found for selector: "${this.config.selector}"`
      );
    }

    // 2. Inject base styles
    this.styleManager.injectBaseStyles();

    // 3. Initialize session
    try {
      await this.sessionManager.initSession(this.config.userId);
    } catch (err) {
      console.error('[GarderoboSearch] Session init failed:', err);
    }

    // 4. Initialize services
    this.searchService = new SearchService(
      this.apiClient, this.stateManager, this.eventBus, this.config, this.filterService
    );
    this.suggestService = new SuggestService(
      this.apiClient, this.stateManager, this.eventBus, this.config
    );

    // 5. Build UI
    this._buildUI();

    // 6. Fetch and apply widget-params
    const rootEl = this.panel ? this.panel.getRootEl() : null;
    this._widgetParams = await this.widgetParamsService.fetchAndApply(rootEl);

    // 7. Attach input handlers on target element
    this._attachTargetHandlers();

    // 8. Wire service events
    this._wireServiceEvents();

    // 9. Show panel (remove display:none — FOUC prevention complete)
    if (this.panel) {
      this.panel.show();
    }

    console.log('[GarderoboSearch] Initialized successfully');
  }

  /** @private Build all UI components */
  _buildUI() {
    // Panel with toolbar + sidebar + content
    this.panel = new Panel(this._targetElement, this.config, this.eventBus, this.stateManager);
    this.panel.render();

    const toolbar = this.panel.getToolbarEl();
    const sidebar = this.panel.getSidebarEl();
    const content = this.panel.getContentEl();

    // Filters label → toolbar (left side, same width as sidebar)
    this.panel.addFiltersLabel(this.i18n);

    // SearchBar → toolbar
    if (this.config.showCustomSearchField) {
      this.searchBar = new SearchBar(this.config, this.eventBus, this.i18n);
      this.searchBar.render(toolbar);
    }

    // Close button → toolbar (after search bar)
    this.panel.addCloseButton(this.i18n);

    // SuggestDropdown → relative to input wrapper (so it aligns exactly with the input)
    this.suggestDropdown = new SuggestDropdown(this.eventBus, this.i18n);
    const suggestContainer = this.searchBar ? this.searchBar.getInputWrapEl() : toolbar;
    this.suggestDropdown.render(suggestContainer);

    // FiltersBar → sidebar
    this.filtersBar = new FiltersBar(this.eventBus, this.stateManager, this.i18n);
    this.filtersBar.render(sidebar);
    this.filtersBar.hide();

    // ResultsGrid → content
    this.resultsGrid = new ResultsGrid(this.eventBus, this.i18n);
    this.resultsGrid.render(content);
    this.resultsGrid.hide();

    // State views → content
    this.loadingState = new LoadingState();
    this.loadingState.render(content);
    this.loadingState.hide();

    this.emptyState = new EmptyState(this.i18n);
    this.emptyState.render(content);
    this.emptyState.hide();

    this.errorState = new ErrorState(this.eventBus, this.i18n);
    this.errorState.render(content);
    this.errorState.hide();

    // CropOverlay (rendered on demand)
    this.cropOverlay = new CropOverlay(this.eventBus, this.i18n);

    // PhotoUpload
    this.photoUpload = new PhotoUpload(this.eventBus);
    this.photoUpload.render();

    // Wire internal UI events
    this._wireUIEvents();
  }

  /** @private Wire EventBus listeners for UI coordination */
  _wireUIEvents() {
    // Photo trigger from search bar
    this.eventBus.on('photo:trigger', () => {
      if (this._widgetParams && this._widgetParams.show_photo_search === false) return;
      if (this.photoUpload) this.photoUpload.trigger();
    });

    // Crop start (from product card visual search)
    this.eventBus.on(EVENTS.CROP_START, (data) => {
      if (this._widgetParams && this._widgetParams.show_visual_search === false) return;
      if (this.cropOverlay) {
        this.cropOverlay.open(data.imageUrl, data.genderId || null);
      }
    });

    // State management: show/hide views based on status
    this.stateManager.subscribe((newState, prevState) => {
      if (newState.status !== prevState.status) {
        this._updateViewState(newState.status);
      }
    });

    // Suggest select → fill input and trigger search
    this.eventBus.on(EVENTS.SUGGEST_SELECT, (data) => {
      if (this.searchBar) {
        this.searchBar.setValue(data.text);
      }
      if (this._targetElement && this._targetElement.tagName === 'INPUT') {
        this._targetElement.value = data.text;
      }
      if (this.suggestDropdown) {
        this.suggestDropdown.hide();
      }
      if (this.suggestService) {
        this.suggestService.clear();
      }
    });

    // Panel close → hide suggest
    this.eventBus.on(EVENTS.PANEL_CLOSE, () => {
      if (this.suggestDropdown) this.suggestDropdown.hide();
      if (this.suggestService) this.suggestService.clear();
    });
  }

  /** @private Wire service-related events */
  _wireServiceEvents() {
    // SEARCH_START → trigger text search
    this.eventBus.on(EVENTS.SEARCH_START, (data) => {
      if (this.suggestDropdown) this.suggestDropdown.hide();
      if (this.suggestService) this.suggestService.clear();
      if (this.searchService) {
        this.searchService.searchByText(data.query, {
          category_id: data.category_id || null,
          vendor_id: data.vendor_id || null,
          type: data.type || null,
        });
      }
    });

    // SEARCH_RESULTS → update UI
    this.eventBus.on(EVENTS.SEARCH_RESULTS, (data) => {
      if (this.resultsGrid) {
        this.resultsGrid.update(data.products, data.totalCount, data.currency, this._widgetParams);
      }
      if (this.filtersBar) {
        const state = this.stateManager.getState();
        this.filtersBar.update(data.facets, state.filters);
      }
      if (this.searchBar) {
        this.searchBar.setLoading(false);
      }
      this._updateFiltersBadge();
    });

    // SEARCH_ERROR → retry callback
    this.eventBus.on(EVENTS.SEARCH_ERROR, () => {
      if (this.searchBar) {
        this.searchBar.setLoading(false);
      }
    });
    if (this.errorState) {
      this.errorState.onRetry(() => {
        if (this.searchService) {
          this.searchService.reSearch();
        }
      });
    }

    // FILTER_CHANGE → update filters and re-search
    this.eventBus.on(EVENTS.FILTER_CHANGE, (data) => {
      this.filterService.setFilter(data);
      this._updateFiltersBadge();
      if (this.searchService) {
        this.searchService.reSearch();
      }
    });

    // FILTER_RESET → reset filters and re-search
    this.eventBus.on(EVENTS.FILTER_RESET, () => {
      this.filterService.resetFilters();
      this._updateFiltersBadge();
      if (this.searchService) {
        this.searchService.reSearch();
      }
    });

    // PHOTO_UPLOAD → search by photo
    this.eventBus.on(EVENTS.PHOTO_UPLOAD, (data) => {
      if (this.searchService) {
        this.searchService.searchByPhoto(data.file);
      }
    });

    // CROP_COMPLETE → search by crop
    this.eventBus.on(EVENTS.CROP_COMPLETE, (data) => {
      if (this.searchService) {
        this.searchService.searchByCrop(data.imageUrl, data.bbox, data.genderId);
      }
    });

    // SUGGEST — input:change triggers suggestions
    this.eventBus.on('input:change', (data) => {
      if (this.suggestService) {
        this.suggestService.suggest(data.value);
      }
    });

    // SUGGEST_RESULTS → update dropdown
    this.eventBus.on(EVENTS.SUGGEST_RESULTS, (data) => {
      if (this.suggestDropdown) {
        this.suggestDropdown.update(data.suggestions);
      }
    });

    // SUGGEST_SELECT → trigger search with suggest context
    this.eventBus.on(EVENTS.SUGGEST_SELECT, (data) => {
      if (this.searchService) {
        this.searchService.searchByText(data.text, {
          category_id: data.category_id || null,
          vendor_id: data.vendor_id || null,
          type: data.type || null,
        });
      }
    });
  }

  /** @private Show the correct view based on state status */
  _updateViewState(status) {
    // Hide all state views
    if (this.loadingState) this.loadingState.hide();
    if (this.emptyState) this.emptyState.hide();
    if (this.errorState) this.errorState.hide();
    if (this.resultsGrid) this.resultsGrid.hide();
    if (this.filtersBar) this.filtersBar.hide();

    switch (status) {
      case 'loading':
        if (this.loadingState) this.loadingState.show();
        if (this.searchBar) this.searchBar.setLoading(true);
        break;
      case 'results':
        if (this.resultsGrid) this.resultsGrid.show();
        if (this.filtersBar) this.filtersBar.show();
        break;
      case 'empty':
        if (this.emptyState) this.emptyState.show();
        break;
      case 'error':
        if (this.errorState) this.errorState.show();
        break;
    }
  }

  /** @private Count active filters and update the badge on the panel */
  _updateFiltersBadge() {
    if (!this.panel) return;
    const filters = this.stateManager.getState().filters || {};
    let count = 0;
    if (filters.category_ids && filters.category_ids.length) count += filters.category_ids.length;
    if (filters.gender_ids && filters.gender_ids.length) count += filters.gender_ids.length;
    if (filters.colors && filters.colors.length) count += filters.colors.length;
    if (filters.sizes && filters.sizes.length) count += filters.sizes.length;
    if (filters.price_min != null) count++;
    if (filters.price_max != null) count++;
    this.panel.updateFiltersBadge(count);
  }

  /** @private Attach handlers to the original target element */
  _attachTargetHandlers() {
    if (!this._targetElement) return;

    this._targetElement.addEventListener('focus', this._onTargetFocus);
    this._targetElement.addEventListener('click', this._onTargetFocus);

    if (this._targetElement.tagName === 'INPUT') {
      this._targetElement.addEventListener('input', this._onTargetInput);
      this._targetElement.addEventListener('keydown', this._onTargetKeydown);
    }

    this._targetForm = this._targetElement.closest('form');
    if (this._targetForm) {
      this._targetForm.addEventListener('submit', this._onFormSubmit);
    }
  }

  /** @private */
  _handleTargetFocus() {
    if (this.panel && !this.panel.isOpen()) {
      this.panel.open();
      if (this.searchBar) {
        this.searchBar.focus();
      }
    }
  }

  /** @private */
  _handleTargetInput() {
    if (this.searchBar && this._targetElement.tagName === 'INPUT') {
      this.searchBar.setValue(this._targetElement.value);
    }
    this.eventBus.emit('input:change', { value: this._targetElement.value });
  }

  /** @private */
  _handleTargetKeydown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      const query = this._targetElement.value.trim();
      if (query) {
        if (!this.panel.isOpen()) this.panel.open();
        this.eventBus.emit(EVENTS.SEARCH_START, { query });
      }
    }

    if (this.suggestDropdown && this.suggestDropdown.isVisible()) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.suggestDropdown.navigate('down');
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.suggestDropdown.navigate('up');
      }
    }
  }

  /** @private */
  _handleFormSubmit(e) {
    e.preventDefault();
  }

  /**
   * Destroy the widget
   */
  destroy() {
    if (this._destroyed) return;
    this._destroyed = true;

    this.apiClient.abortAll();

    if (this.searchService) { this.searchService.destroy(); this.searchService = null; }
    if (this.suggestService) { this.suggestService.destroy(); this.suggestService = null; }
    this.filterService = null;
    this.widgetParamsService = null;

    if (this._targetElement) {
      this._targetElement.removeEventListener('focus', this._onTargetFocus);
      this._targetElement.removeEventListener('click', this._onTargetFocus);
      this._targetElement.removeEventListener('input', this._onTargetInput);
      this._targetElement.removeEventListener('keydown', this._onTargetKeydown);
    }
    if (this._targetForm) {
      this._targetForm.removeEventListener('submit', this._onFormSubmit);
      this._targetForm = null;
    }

    if (this.panel) { this.panel.destroy(); this.panel = null; }
    if (this.searchBar) { this.searchBar.destroy(); this.searchBar = null; }
    if (this.suggestDropdown) { this.suggestDropdown.destroy(); this.suggestDropdown = null; }
    if (this.filtersBar) { this.filtersBar.destroy(); this.filtersBar = null; }
    if (this.resultsGrid) { this.resultsGrid.destroy(); this.resultsGrid = null; }
    if (this.cropOverlay) { this.cropOverlay.destroy(); this.cropOverlay = null; }
    if (this.photoUpload) { this.photoUpload.destroy(); this.photoUpload = null; }
    if (this.loadingState) { this.loadingState.destroy(); this.loadingState = null; }
    if (this.emptyState) { this.emptyState.destroy(); this.emptyState = null; }
    if (this.errorState) { this.errorState.destroy(); this.errorState = null; }

    this.styleManager.destroy();
    this.sessionManager.clearSession();
    this.eventBus.clear();
    this.stateManager.reset();
    this._targetElement = null;

    console.log('[GarderoboSearch] Destroyed');
  }
}
